import moment from 'moment-timezone';
import hasDateAsTimestampInJson from 'hasDateAsTimestampInJson';
import camelCaseKeys from 'camelcase-keys';
import sortBy from 'Utils/sortBy';
import keyBy from 'Utils/keyBy';
import angularModule from './cohorts_module';

angularModule.factory('Cohort', [
    '$injector',
    $injector => {
        const Iguana = $injector.get('Iguana');
        const TranslationHelper = $injector.get('TranslationHelper');
        const dateHelper = $injector.get('dateHelper');
        const Schedulable = $injector.get('Schedulable');

        // oxlint-disable-next-line func-names
        return Iguana.subclass(function () {
            this.setCollection('cohorts');
            this.alias('Cohort');
            this.setIdProperty('id');
            this.include(Schedulable);

            this.embedsMany('cohort_sections', 'CohortSection');

            hasDateAsTimestampInJson(this, 'startDate');

            // oxlint-disable-next-line func-names
            this.setCallback('after', 'copyAttrsOnInitialize', function () {
                this.specialization_playlist_pack_ids = this.specialization_playlist_pack_ids || [];
                this.periods = this.periods || [];
                this.start_date = this.start_date || this.computeDefaultStartDate().getTime() / 1000;

                // Sort the admission_rounds
                // FIXME: Check if there is an Iguana way to do this
                this.admission_rounds = sortBy(this.admission_rounds, 'application_deadline');

                // Fill out dates for all the periods
                this.computePeriodDates();

                this.slack_rooms = this.slack_rooms || [];
                this.cohort_sections = this.cohort_sections || [];

                this.setDefaultEnrollmentDocumentsDeadline();
                this.setDefaultOfficialTranscriptsDeadline();

                // For use in es6 functions
                this.idVerificationPeriods = this.id_verification_periods?.map(period => camelCaseKeys(period));
                this.enrollmentDeadlineDaysOffset = this.enrollment_deadline_days_offset;
            });

            this.VALID_STATUSES = ['closed', 'open'];

            this.DEFAULT_REGISTRATION_DEADLINE_DAYS_OFFSET = -7; // see also cohort.rb and `defaultAutoDeclineAdmissionOffersDaysOffset` in ProgramTypeConfigs.ts

            this.extend({
                // Given an admission round, a cohort that includes that admission round, and a set of cohorts with that cohort, determine the previous
                // admission round if possible
                getPreviousAdmissionRound(admissionRound, cohort, cohorts) {
                    // Find the index of the admission round we want the previous for
                    const admissionRoundIndex = (cohort.admission_rounds || []).indexOf(admissionRound);

                    // If it is not the first then just snag the one before it. Easy peasy.
                    if (admissionRoundIndex > 0) {
                        return cohort.admission_rounds[admissionRoundIndex - 1].applicationDeadline;
                    }
                    if (admissionRoundIndex === 0) {
                        // If it is the first admission around then we need to go to the previous cohort's (of the same type)
                        // last admission round.
                        cohorts = cohorts.filter(c => c.program_type === cohort.program_type);
                        cohorts = sortBy(cohorts, 'startDate');

                        // Find the cohort that we want the previous for
                        const cohortIndex = cohorts.indexOf(cohort);

                        if (cohortIndex > 0) {
                            const previousCohort = cohorts[cohortIndex - 1];
                            return previousCohort.admission_rounds?.length
                                ? previousCohort.admission_rounds[previousCohort.admission_rounds.length - 1]
                                      .applicationDeadline
                                : null;
                        }
                        // There is no cohort to get a previous admission round for
                        return null;
                    }
                    // oxlint-disable-next-line no-console
                    console.warn('Could not find admission round');
                    return null;
                },
            });

            Object.defineProperty(this.prototype, 'currentPeriod', {
                get() {
                    if (
                        this.current_period_index === null ||
                        this.current_period_index === undefined ||
                        this.current_period_index < 0
                    ) {
                        return null;
                    }

                    return this.periods[this.current_period_index];
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'foundationsPlaylistLocalePackId', {
                get() {
                    return this.foundations_playlist_locale_pack ? this.foundations_playlist_locale_pack.id : null;
                },
            });

            Object.defineProperty(this.prototype, 'isUnfinishedDegreeProgram', {
                get() {
                    return this.isDegreeProgram && this.endDate > new Date();
                },
            });

            Object.defineProperty(this.prototype, 'isFinishedDegreeProgram', {
                get() {
                    return this.isDegreeProgram && this.endDate <= new Date();
                },
            });

            Object.defineProperty(this.prototype, 'createdAt', {
                get() {
                    return new Date(this.created_at * 1000);
                },
            });

            Object.defineProperty(this.prototype, 'updatedAt', {
                get() {
                    return new Date(this.updated_at * 1000);
                },
            });

            // See also cohort.rb#trial_end_date
            Object.defineProperty(this.prototype, 'trialEndDate', {
                get() {
                    if (!this.$$trialEndDate) {
                        this.$$trialEndDate = new Date(this.trial_end_date * 1000);
                    }
                    return this.$$trialEndDate;
                },
                configurable: true, // specs
            });

            Object.defineProperty(this.prototype, 'publishedAt', {
                get() {
                    return (this.published_at && new Date(1000 * this.published_at)) || undefined;
                },
            });

            ['exam', 'review', 'break', 'specialization', 'project'].forEach(style => {
                Object.defineProperty(this.prototype, `${style}Periods`, {
                    get() {
                        return this.periods.filter(period => period.style === style);
                    },
                });
            });

            Object.defineProperty(this.prototype, 'requiredStreamPackIdsCache', {
                get() {
                    const self = this;
                    if (!self._requiredStreamPackIdsCache) {
                        self._requiredStreamPackIdsCache = {};
                        self.getRequiredStreamPackIdsFromPeriods().forEach(localePackId => {
                            self._requiredStreamPackIdsCache[localePackId] = true;
                        });
                    }
                    return self._requiredStreamPackIdsCache;
                },
                configurable: true,
            });

            /*
                This date only applies to users who have deferred from a previous Cohort and are not registered.
                They will not be able to register for their new Cohort until 180 days before the registration deadline.
                Admission offers are not sent out until well after the registrationOpenDate anyway, so this date is not
                important for people receiving a fresh admission offer.
            */
            Object.defineProperty(this.prototype, 'registrationOpenDate', {
                get() {
                    return new Date(moment(this.registrationDeadline).subtract(180, 'days'));
                },
                configurable: true,
            });

            // See also the Cohort#after_registration_open_date? method on the server where this logic is duplicated.
            Object.defineProperty(this.prototype, 'afterRegistrationOpenDate', {
                get() {
                    if (this.isExecEd) return true; // HACK: https://trello.com/c/gbvEER9G

                    return moment().valueOf() >= moment(this.registrationOpenDate).startOf('day').valueOf();
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'inEarlyAdmissionsRegistrationPeriod', {
                get() {
                    if (this.isExecEd) return false;

                    return (
                        moment().valueOf() < moment(this.lastDecisionDate).startOf('day').subtract(1, 'days').valueOf()
                    );
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'registrationDeadlineHasPassed', {
                get() {
                    return moment().valueOf() >= moment(this.registrationDeadline).valueOf();
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'completionDeadline', {
                get() {
                    if (!this.completion_deadline) {
                        return null;
                    }
                    return new Date(this.completion_deadline * 1000);
                },
            });

            Object.defineProperty(this.prototype, 'hasVerificationPeriods', {
                get() {
                    return (this.id_verification_periods || []).length > 0;
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'isLegacyEnrollmentCohort', {
                get() {
                    // special handling for EMBA10 and earlier / MBA15 and earlier where we don't necessarily
                    // want to corrupt the notion of receiving an enrollment agreement by updating the backing data,
                    // but want to indicate that these applications had gone through a similar / comparable process
                    return (
                        (this.program_type === 'mba' && this.startDate < new Date('2018/08/21')) ||
                        (this.program_type === 'emba' && this.startDate < new Date('2018/07/17'))
                    );
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'learnerProjectIdsFromPeriods', {
                get() {
                    return [...new Set(this.periods.map(period => period.learner_project_ids).flat(Infinity))].filter(
                        Boolean,
                    );
                },
            });

            Object.defineProperty(this.prototype, 'lastDecisionDate', {
                get() {
                    const lastAdmissionRound = this.admission_rounds.at(-1);
                    return lastAdmissionRound && lastAdmissionRound.decisionDate;
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'programGuideUrl', {
                get() {
                    return this.program_guide_url;
                },
            });

            Object.defineProperty(this.prototype, 'hasSlackRooms', {
                get() {
                    return this.slack_rooms ? this.slack_rooms.length > 0 : false;
                },
            });

            Object.defineProperty(this.prototype, 'requiresMailingAddress', {
                get() {
                    // diplomas are physical objects tht we need to be able to mail.
                    // If this logic ever changes, see sidebarBoxConfigs -> SidebarBoxKey.diploma_check -> desktopContentHtml
                    return this.supportsPhysicalDiploma;
                },
            });

            return {
                persistsEndDate: true,

                getConcentrationPlaylists(playlists) {
                    playlists = keyBy(playlists, 'localePackId');
                    return (this.concentrationPlaylistPackIds || [])
                        .map(localePackId => playlists[localePackId])
                        .filter(Boolean);
                },

                getSpecializationPlaylists(playlists) {
                    playlists = keyBy(playlists, 'localePackId');
                    return (this.specialization_playlist_pack_ids || [])
                        .map(localePackId => playlists[localePackId])
                        .filter(Boolean);
                },

                isStreamInFoundationsPlaylist(streamLocalePackId) {
                    return this.foundations_lesson_stream_locale_pack_ids.includes(streamLocalePackId);
                },

                lastAdmissionRoundDeadlineHasPassed() {
                    if (!this.supportsAdmissionRounds) {
                        return false;
                    }

                    return !this.getApplicableAdmissionRound();
                },

                admissionRoundDeadlineHasPassed(appliedAt) {
                    if (!this.supportsAdmissionRounds) {
                        return false;
                    }

                    const applicableAdmissionRound = this.getApplicableAdmissionRound(appliedAt);
                    return !applicableAdmissionRound || applicableAdmissionRound.applicationDeadline <= Date.now();
                },

                getApplicableAdmissionRound(appliedAt) {
                    if (appliedAt) {
                        // There are known cases of users who apply for a cohort, but ultimately end up being placed in a previous cohort (typically at the request of the user).
                        // In these situations, the appliedAt value on the program application will be a more recent date than all of the application deadlines for that cohort's
                        // admission rounds. This can cause getApplicableAdmissionRound to return null, which can cause various issues for users, such as not being able to edit their
                        // application prior to admission. To prevent this, we should simply return the final admission round of the cohort.
                        return (
                            this.admission_rounds.find(
                                admissionRound => appliedAt < admissionRound.applicationDeadline,
                            ) ?? this.admission_rounds.at(-1)
                        );
                    }

                    const now = new Date();
                    if (this.startDate < now) {
                        return null;
                    }
                    return this.admission_rounds.find(admissionRound => admissionRound.applicationDeadline > now);
                },

                applicationDeadline() {
                    const applicableAdmissionRound = this.getApplicableAdmissionRound();
                    return applicableAdmissionRound?.applicationDeadline;
                },

                /*
                    The `settings.application_status.*` locale keys these read are written in ICU (single-brace)
                    syntax so that both React (i18next-icu) and Angular can share one copy of each string. That
                    is why these calls pass 'messageformat' as the TranslationHelper interpolationId.
                */
                getApplicationDeadlineMessage(appliedAt) {
                    // Create default message about the deadline
                    const translationHelper = new TranslationHelper('settings.application_status'); // just cause it is already there

                    if (!this.supportsAdmissionRounds) {
                        return translationHelper.get(
                            'deadline_message_rolling_basis',
                            undefined,
                            undefined,
                            'messageformat',
                        );
                    }

                    const applicableAdmissionRound = this.getApplicableAdmissionRound(appliedAt);

                    if (!applicableAdmissionRound) {
                        return null;
                    }

                    let localeKey;

                    // In most cases, students will not be viewing this locale string after the decision date. See
                    // exceptions on https://trello.com/c/ngU07aP2. We only show the decision date part of the message
                    // when the user has applied and the decision date is in the future.
                    const showDecisionDate = !!appliedAt && applicableAdmissionRound.decisionDate > Date.now();
                    const withDecisionDate = showDecisionDate ? '_with_decision_date' : '';

                    // This is partially tested in cohort_spec.js and partially in ApplicationStatus.spec.tsx
                    if (this.admissionRoundDeadlineHasPassed(appliedAt) && this.admission_rounds.length > 1) {
                        // multiple rounds (closed)
                        localeKey = `application_deadline_multiple_rounds_closed${
                            applicableAdmissionRound.index + 1 === this.admission_rounds.length ? '_final' : ''
                        }${withDecisionDate}`;
                    } else if (this.admissionRoundDeadlineHasPassed(appliedAt)) {
                        // single round (closed)
                        localeKey = `application_deadline_single_round_closed${withDecisionDate}`;
                    } else if (this.admission_rounds.length > 1) {
                        // multiple rounds (not closed)
                        localeKey = `application_deadline_multiple_rounds${
                            applicableAdmissionRound.index + 1 === this.admission_rounds.length ? '_final' : ''
                        }${withDecisionDate}`;
                    } else {
                        // single round (not closed)
                        localeKey = `application_deadline_single_round${withDecisionDate}`;
                    }

                    const applicationDeadlineMessage = translationHelper.get(
                        localeKey,
                        {
                            roundIndex: applicableAdmissionRound.index + 1,
                            applicationDeadline: dateHelper.formattedUserFacingMonthDayYearLong(
                                applicableAdmissionRound.applicationDeadline,
                            ),
                            decisionDate: dateHelper.formattedUserFacingMonthDayYearLong(
                                applicableAdmissionRound.decisionDate,
                                false,
                            ),
                        },
                        undefined,
                        'messageformat',
                    );

                    return applicationDeadlineMessage;
                },

                getRequiredStreamPackIdsFromPeriods(maxPeriod) {
                    if (angular.isUndefined(maxPeriod)) {
                        maxPeriod = this.periods.length - 1;
                    }

                    if (!this.supportsSchedule || maxPeriod < 0) {
                        return [];
                    }

                    return [
                        ...new Set(
                            this.periods
                                .slice(0, maxPeriod + 1)
                                .map(period => period.requiredStreamLocalePackIds)
                                .flat(Infinity),
                        ),
                    ];
                },

                getStreamPackIdsFromSpecializations(playlists) {
                    if (!playlists) {
                        throw new Error(
                            'Cannot determine curriculum order of specialization stream pack ids without playlists',
                        );
                    }

                    return [
                        ...new Set(
                            this.getSpecializationPlaylists(playlists)
                                .map(playlist => playlist.streamLocalePackIds)
                                .flat(),
                        ),
                    ];
                },

                // Returns an array that first has all of the stream pack ids from the schedule in the
                // order they appear in the schedule, and then has all of the stream pack ids from the
                // specializations in the order that the specializations appear in specialization_playlist_pack_ids
                getStreamPackIdsInCurriculumOrder(playlists) {
                    if (!this.supportsSchedule) {
                        return this.getStreamPackIdsFromPlaylistCollections(playlists);
                    }

                    return this.getRequiredStreamPackIdsFromPeriods().concat(
                        this.getStreamPackIdsFromSpecializations(playlists),
                    );
                },
            };
        });
    },
]);
